import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import backofficeApiClient, {
  AssignmentAdditionalInfoModel,
  AssignmentAdditionalInfoReplyRequestModel,
  AssignmentHideIndicatorModel,
  AssignmentInfoUserResponseModel,
  AssignmentPrimaryDocumentEditModel,
  AssignmentStatusTypeEnum,
  RenewAssignmentModel,
  AssignmentFinancialSchedulerEditModel,
  FileTypeEnum,
  AssignmentTerminationCreateUpdateApiModel,
  PayRateTypeModel,
  PayCodeModel,
  RenewKindEnum,
  RenewAssignmentEnum,
  WorkerTypeEnum,
} from 'api/backofficeApiClient';
import adminApiClient, {
  AssignmentAdminDocumentModel,
  AssignmentApproveStepModel,
  AssignmentBulkImportViewModel,
  AssignmentInfoAdminResponseModel,
  AssignmentQuestionnaireInfoModel,
  AssignmentStageFilterEnum,
  AssignmentsTypePayCodes,
  AssignmentTerminationReasonTypeEnum,
  KronosImportTypeEnum,
  PayCodesGroup,
} from 'api/adminApiClient';
import { logout } from './authenticationSlice';
import { AssignmentDetailsModel } from 'utils/assignmentUtils';
import marketplaceApiClient, { JobTitleEntity } from 'api/marketplaceApiClient';
import { isAnyActionOf } from '../utils';

interface AssignmentState {
  jobTitles: JobTitleEntity[];
  isJobTitlesLoading: boolean;
  workCodes: string[];
  isWorkCodesLoading: boolean;
  payRateTypes: PayRateTypeModel[];
  payCodes: PayCodeModel[];
  isPayCodeTypesLoading: boolean;
  typesPayCode?: AssignmentsTypePayCodes[];
  payCodesGroups?: PayCodesGroup[];
  isPayCodesGroupsLoading: boolean;
  isTypesPayCodeLoading: boolean;
  assignmentCreatedAt?: number;
  assignmentDraftCreatedAt?: number;
  assignmentDetails?: AssignmentDetailsModel;
  isTemplateAssignmentModalOpen: boolean;
  assignmentTemplateCreatedAt?: number;
  assignmentDetailsUpdatedAtByAdmin?: number;
}

const initialState: AssignmentState = {
  jobTitles: [],
  workCodes: [],
  isWorkCodesLoading: false,
  isJobTitlesLoading: false,
  payRateTypes: [],
  payCodes: [],
  typesPayCode: undefined,
  payCodesGroups: [],
  isPayCodesGroupsLoading: false,
  isPayCodeTypesLoading: false,
  isTypesPayCodeLoading: false,
  assignmentCreatedAt: undefined,
  assignmentDraftCreatedAt: undefined,
  assignmentDetails: undefined,
  isTemplateAssignmentModalOpen: false,
  assignmentTemplateCreatedAt: undefined,
  assignmentDetailsUpdatedAtByAdmin: undefined,
};

interface CreateNewAssignmentFromDraftModel {
  assignmentId: string;
  data: AssignmentPrimaryDocumentEditModel;
}

const getJobTitles = createAsyncThunk('assignment/getJobTitles', async () => {
  return marketplaceApiClient.getJobTitles();
});

const getAllWorkCodes = createAsyncThunk(
  'assignment/getAllWorkCodes',
  async (isAdmin: boolean) => {
    return isAdmin
      ? adminApiClient.getAllWorkCodesSimpleList()
      : backofficeApiClient.getAllWorkCodesSimpleList();
  },
);

const getPayRateTypes = createAsyncThunk(
  'assignment/getPayRateTypes',
  async (isAdmin: boolean) => {
    return isAdmin
      ? adminApiClient.getPayRateTypes()
      : backofficeApiClient.getPayRateTypes();
  },
);

const getPayCodesDetailsInfo = createAsyncThunk(
  'assignment/getPayCodesDetailsInfo',
  async (isAdminRole: boolean) => {
    return isAdminRole
      ? adminApiClient.getPayCodes(undefined)
      : backofficeApiClient.getPayCodes(undefined);
  },
);

const getAssignmentTypesPayCode = createAsyncThunk(
  'assignment/getAssignmentTypesPayCode',
  async (isAdminRole: boolean) => {
    return isAdminRole
      ? adminApiClient.getAssignmentTypesPayCode()
      : backofficeApiClient.getAssignmentTypesPayCode();
  },
);

const getAssignmentPayCodeGroups = createAsyncThunk(
  'assignment/getAssignmentPayCodeGroups',
  async (isAdminRole: boolean) => {
    return isAdminRole
      ? adminApiClient.getPayCodeGroups()
      : backofficeApiClient.getPayCodeGroups();
  },
);

const createNewAssignment = createAsyncThunk(
  'assignment/createNewAssignment',
  async (data: AssignmentPrimaryDocumentEditModel, { rejectWithValue }) => {
    try {
      const response = await backofficeApiClient.createNewAssignment(data);

      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const createNewAssignmentFromDraft = createAsyncThunk(
  'assignment/createNewAssignmentFromDraft',
  async (
    { assignmentId, data }: CreateNewAssignmentFromDraftModel,
    { rejectWithValue },
  ) => {
    try {
      const response = await backofficeApiClient.migrateDraftAssignment(
        assignmentId,
        data,
      );

      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const createNewDraftAssignment = createAsyncThunk(
  'assignment/createNewDraftAssignment',
  async (data: AssignmentPrimaryDocumentEditModel, { rejectWithValue }) => {
    try {
      const response = await backofficeApiClient.createNewDraftAssignment(data);

      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const updateDraftAssignment = createAsyncThunk(
  'assignment/updateDraftAssignment',
  async (
    { assignmentId, data }: CreateNewAssignmentFromDraftModel,
    { rejectWithValue },
  ) => {
    try {
      const response = await backofficeApiClient.updateDraftAssignment(
        assignmentId,
        data,
      );

      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const updateAssignmentByAdmin = createAsyncThunk(
  'assignment/updateAssignmentByAdmin',
  async (
    { assignmentId, data }: CreateNewAssignmentFromDraftModel,
    { rejectWithValue },
  ) => {
    try {
      const response = await adminApiClient.updateAssignmentPrimaryDocument(
        assignmentId,
        data,
      );

      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const updateAssignment = createAsyncThunk(
  'assignment/updateAssignment',
  async (
    { assignmentId, data }: CreateNewAssignmentFromDraftModel,
    { rejectWithValue },
  ) => {
    try {
      return await backofficeApiClient.updateAssignmentPrimaryDocument(
        assignmentId,
        data,
      );
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const updateTemplate = createAsyncThunk(
  'assignment/updateTemplate',
  async (
    { assignmentId, data }: CreateNewAssignmentFromDraftModel,
    { rejectWithValue },
  ) => {
    try {
      return await backofficeApiClient.updateTemplate(assignmentId, data);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

interface AssignmentListModel {
  requestId: number;
  pageNumber: number;
  isAdminRole?: boolean;
  companyIds?: string[];
  searchQuery?: string;
  stage?: AssignmentStageFilterEnum;
  statuses?: AssignmentStatusTypeEnum[];
  customerIds?: string[];
  workerIds?: string[];
  workerTypes?: WorkerTypeEnum[];
  terminationReasons?: AssignmentTerminationReasonTypeEnum[];
  startDate?: Date;
  endDate?: Date;
}

const getAssignments = createAsyncThunk(
  'assignment/getAssignments',
  async ({
    pageNumber,
    statuses,
    stage,
    searchQuery,
    isAdminRole,
    companyIds,
    customerIds,
    workerIds,
    workerTypes,
    terminationReasons,
    startDate,
    endDate,
  }: AssignmentListModel) => {
    return isAdminRole
      ? adminApiClient.getAssignmentsPagedList(
          pageNumber,
          searchQuery,
          statuses,
          stage,
          companyIds,
          customerIds,
          workerIds,
          terminationReasons,
          startDate,
          endDate,
          workerTypes,
        )
      : backofficeApiClient.getAssignments(
          pageNumber,
          searchQuery,
          statuses,
          stage,
          customerIds,
          workerIds,
          workerTypes,
          terminationReasons,
        );
  },
);

const getAssignmentListExcel = createAsyncThunk(
  'assignment/getAssignmentListExcel',
  async () => {
    const result = await adminApiClient.getAssignmentsListExcel();
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

interface GetAssignmentDetailsByIdModel {
  assignmentId: string;
  isAdminRole?: boolean;
}

interface GetAssignmentDetailsModel {
  assignmentId?: string;
  assignmentKey: string;
  isAdminRole?: boolean;
}

const getAssignmentPrimaryDetailsByRoleByKey = ({
  assignmentKey = '',
  isAdminRole,
}: GetAssignmentDetailsModel) => {
  return isAdminRole
    ? adminApiClient.getAssignmentDetailByKey(assignmentKey)
    : backofficeApiClient.getAssignmentDetailByKey(assignmentKey);
};

const getAssignmentPrimaryDetailsByKey = createAsyncThunk(
  'assignment/getAssignmentPrimaryDetailsByKey',
  async (options: GetAssignmentDetailsModel) =>
    getAssignmentPrimaryDetailsByRoleByKey(options),
);

const getAssignmentPrimaryDetails = createAsyncThunk(
  'assignment/getAssignmentPrimaryDetails',
  async ({ assignmentKey, isAdminRole }: GetAssignmentDetailsModel) => {
    return isAdminRole
      ? adminApiClient.getAssignmentDetail(assignmentKey)
      : backofficeApiClient.getAssignmentDetail(assignmentKey);
  },
);

const getAssignmentsCompanies = createAsyncThunk(
  'assignment/getAssignmentsCompanies',
  async (assignmentStatuses: AssignmentStatusTypeEnum[]) => {
    return adminApiClient.getAssignmentsCompanies(assignmentStatuses);
  },
);

interface GetAssignmentCustomersModel {
  assignmentStatuses: AssignmentStatusTypeEnum[];
  isAdminRole?: boolean;
}

const getCustomersByAssignments = createAsyncThunk(
  'assignment/getCustomersByAssignments',
  async ({ assignmentStatuses, isAdminRole }: GetAssignmentCustomersModel) => {
    return isAdminRole
      ? adminApiClient.getCustomersByAssignments(assignmentStatuses)
      : backofficeApiClient.getCustomersByAssignments(assignmentStatuses);
  },
);

const getWorkersByAssignments = createAsyncThunk(
  'assignment/getWorkersByAssignments',
  async (isAdminRole: boolean) => {
    return isAdminRole
      ? adminApiClient.getWorkerLookups()
      : backofficeApiClient.getWorkerLookups();
  },
);

const approveAssignmentProgressStep = createAsyncThunk(
  'assignment/approveAssignmentProgressStep',
  async (stepData: AssignmentApproveStepModel) => {
    return adminApiClient.assignmentApproveStep(stepData);
  },
);

const completeAssignmentProgressStep = createAsyncThunk(
  'assignment/completeAssignmentProgressStep',
  async (stepData: AssignmentApproveStepModel) => {
    await adminApiClient.assignmentApproveStep(stepData);

    return adminApiClient.getAssignmentDetail(stepData.assignmentId as string);
  },
);

const sendAssignmentAdditionalInfoResponse = createAsyncThunk(
  'assignment/sendAssignmentAdditionalInfoResponse',
  async (responseInfo: AssignmentAdditionalInfoReplyRequestModel) => {
    const { assignmentId } = responseInfo;

    await backofficeApiClient.sendAssignmentAdditionalInfoReply(responseInfo);

    return backofficeApiClient.getAssignmentDetail(assignmentId);
  },
);

const hideAssignmentPulsatingIndicator = createAsyncThunk(
  'assignment/hideAssignmentPulsatingIndicator',
  async (stageInfo: AssignmentHideIndicatorModel) => {
    const { assignmentId } = stageInfo;
    await backofficeApiClient.hideAssignmentPulsatingIndicator(stageInfo);

    return backofficeApiClient.getAssignmentDetail(assignmentId as string);
  },
);

interface CreateCommentForAssignmentModel {
  entityId: string;
  text: string;
  commentId?: string;
}

const createCommentForAssignment = createAsyncThunk(
  'assignment/createCommentForAssignment',
  async ({ entityId, text }: CreateCommentForAssignmentModel) => {
    return adminApiClient.createNewAssignmentComment({
      assignmentId: entityId,
      text,
    });
  },
);

const updateCommentForAssignment = createAsyncThunk(
  'assignment/updateCommentForAssignment',
  async ({ commentId, text }: CreateCommentForAssignmentModel) => {
    return adminApiClient.updateAssignmentComment({
      id: commentId,
      text,
    });
  },
);

interface CommentsForAssignmentModel {
  entityId: string;
  pageNumber: number;
  requestId: number;
}

const getCommentsForAssignment = createAsyncThunk(
  'assignment/getCommentsForAssignment',
  async ({ entityId, pageNumber }: CommentsForAssignmentModel) => {
    return adminApiClient.getAssignmentComments(pageNumber, entityId);
  },
);

interface GetEditAssignmentModel {
  assignmentId: string;
}

const getEditInfoForAssignmentByAdmin = createAsyncThunk(
  'assignment/getEditInfoForAssignmentByAdmin',
  async ({ assignmentId }: GetEditAssignmentModel) => {
    return adminApiClient.getAssignmentEditModel(assignmentId);
  },
);

const getAssignmentTerminationModel = createAsyncThunk(
  'assignment/getAssignmentTerminationModel',
  async ({
    assignmentId,
    isAdmin,
  }: {
    assignmentId: string;
    isAdmin: boolean;
  }) => {
    return isAdmin
      ? adminApiClient.getAssignmentTerminationModel(assignmentId)
      : backofficeApiClient.getAssignmentTerminationModel(assignmentId);
  },
);

const saveTerminateAssignment = createAsyncThunk(
  'assignment/saveTerminateAssignment',
  async ({
    data,
    isAdmin,
  }: {
    data: AssignmentTerminationCreateUpdateApiModel;
    isAdmin: boolean;
  }) => {
    return isAdmin
      ? adminApiClient.createNewAssignmentTermination(data)
      : backofficeApiClient.createNewAssignmentTermination(data);
  },
);

const deleteAssignmentTermination = createAsyncThunk(
  'assignment/deleteAssignmentTermination',
  async ({
    assignmentId,
    isAdmin,
  }: {
    assignmentId: string;
    isAdmin: boolean;
  }) => {
    return isAdmin
      ? adminApiClient.deleteAssignmentTermination(assignmentId)
      : backofficeApiClient.deleteAssignmentTermination(assignmentId);
  },
);

const updateAssignmentTermination = createAsyncThunk(
  'assignment/updateAssignmentTermination',
  async ({
    data,
    isAdmin,
  }: {
    data: AssignmentTerminationCreateUpdateApiModel;
    isAdmin: boolean;
  }) => {
    return isAdmin
      ? adminApiClient.updateAssignmentTermination(data)
      : backofficeApiClient.updateAssignmentTermination(data);
  },
);

const getAssignmentTerminationAttachmentFileAll = createAsyncThunk(
  'assignment/getAssignmentTerminationAttachmentFile',
  async ({
    assignmentId,
    isAdmin,
  }: {
    assignmentId: string;
    isAdmin: boolean;
  }) => {
    return isAdmin
      ? adminApiClient.getAssignmentTerminationAttachmentFile(assignmentId)
      : backofficeApiClient.getAssignmentTerminationAttachmentFile(
          assignmentId,
        );
  },
);

const getAssignmentTerminationAttachmentFile = createAsyncThunk(
  'assignment/getAssignmentTerminationAttachmentFile',
  async ({ fileKey, fileName }: { fileKey?: string; fileName?: string }) => {
    return adminApiClient.getAssignmentAttachmentFile(fileKey, fileName);
  },
);

const getAssignmentSupportingDocumentFile = createAsyncThunk(
  'assignment/getAssignmentSupportingDocumentFile',
  async ({
    fileKey,
    fileName,
    isAdmin,
  }: {
    fileKey: string;
    fileName: string;
    isAdmin: boolean;
  }) => {
    return isAdmin
      ? adminApiClient.getAssignmentAttachmentFile(fileKey, fileName)
      : backofficeApiClient.getAssignmentAttachmentFile(fileKey, fileName);
  },
);

const getEditInfoForAssignment = createAsyncThunk(
  'assignment/getEditInfoForAssignment',
  async ({ assignmentId }: GetEditAssignmentModel) => {
    return backofficeApiClient.getAssignmentEditModel(assignmentId);
  },
);

const getEditDraftInfoForAssignment = createAsyncThunk(
  'assignment/getEditDraftInfoForAssignment',
  async ({ assignmentId }: GetEditAssignmentModel) => {
    return backofficeApiClient.getAssignmentDraftEditModel(assignmentId);
  },
);

const getRenewInfoForAssignment = createAsyncThunk(
  'assignment/getRenewInfoForAssignment',
  async ({ assignmentId, isAdminRole }: GetAssignmentDetailsByIdModel) => {
    return isAdminRole
      ? adminApiClient.getAssignmentEditModel(assignmentId)
      : backofficeApiClient.getAssignmentDraftEditModel(assignmentId);
  },
);

const getAssignmentAdditionalDetailsByRole = ({
  assignmentId,
  isAdminRole,
}: GetAssignmentDetailsByIdModel) => {
  return isAdminRole
    ? adminApiClient.getAssignmentAdminDocumentEditModel(assignmentId)
    : backofficeApiClient.getAssignmentAdditionalInfo(assignmentId);
};

const getAssignmentAdminAdditionalInfo = createAsyncThunk(
  'assignment/getAssignmentAdditionalInfo',
  async (assignmentId: string) => {
    return adminApiClient.getAssignmentAdminDocumentEditModel(assignmentId);
  },
);

const getAssignmentAdditionalInfo = createAsyncThunk(
  'assignment/getAssignmentMemberAdditionalInfo',
  async (assignmentId: string) => {
    return backofficeApiClient.getAssignmentAdditionalInfo(assignmentId);
  },
);

interface UpdateAssignmentAdditionalInfoModel {
  assignmentId: string;
  data: AssignmentAdminDocumentModel;
}

const updateAssignmentAdditionalInfo = createAsyncThunk(
  'assignment/updateAssignmentAdminDocument',
  async ({ assignmentId, data }: UpdateAssignmentAdditionalInfoModel) => {
    return adminApiClient.updateAssignmentAdminDocument(assignmentId, data);
  },
);

interface SendQuestionnaireOptions {
  isAdminRole?: boolean;
  assignmentId: string;
  assignmentKey: string;
}
const sendMemberQuestionnaireNotification = createAsyncThunk(
  'assignment/sendMemberQuestionnaireNotification',
  async (options: SendQuestionnaireOptions) => {
    const { isAdminRole, assignmentId, assignmentKey } = options;

    await adminApiClient.sendNotificationQuestionnaire(assignmentId);

    return getAssignmentPrimaryDetailsByRoleByKey({
      assignmentKey,
      isAdminRole,
    });
  },
);

const sendClassificationQuestionnaireNotification = createAsyncThunk(
  'assignment/sendClassificationQuestionnaireNotification',
  async (options: SendQuestionnaireOptions) => {
    const { isAdminRole, assignmentKey, assignmentId } = options;

    await adminApiClient.sendContractorNotificationQuestionnaire(assignmentId);

    return getAssignmentPrimaryDetailsByRoleByKey({
      assignmentKey,
      isAdminRole,
    });
  },
);

interface AssignmentInfo {
  primaryInfo:
    | AssignmentInfoUserResponseModel
    | AssignmentInfoAdminResponseModel;
  additionalInfo?: AssignmentAdditionalInfoModel;
}

const getAssignmentDetailsByRole = async ({
  assignmentId,
  assignmentKey,
  isAdminRole,
}: GetAssignmentDetailsModel): Promise<AssignmentInfo> => {
  const primaryInfo = await getAssignmentPrimaryDetailsByRoleByKey({
    assignmentKey,
    isAdminRole,
  });

  const additionalInfo = await getAssignmentAdditionalDetailsByRole({
    assignmentId: assignmentId as string,
    isAdminRole,
  });

  return { primaryInfo, additionalInfo };
};

interface SaveQuestionnaireOptions {
  isAdmin?: boolean;
  assignmentId: string;
  assignmentKey: string;
  quesitonnaireData: AssignmentQuestionnaireInfoModel;
}
const saveMemberQuestionnaire = createAsyncThunk(
  'assignment/saveMemberQuestionnaire',
  async (data: SaveQuestionnaireOptions) => {
    const { isAdmin, assignmentId, assignmentKey, quesitonnaireData } = data;

    isAdmin
      ? await adminApiClient.fillAssignmentQuestionnaireData(
          assignmentId,
          quesitonnaireData,
        )
      : await backofficeApiClient.fillMemberQuestionnaireData(
          assignmentId,
          quesitonnaireData,
        );

    return getAssignmentDetailsByRole({
      assignmentId,
      assignmentKey,
      isAdminRole: isAdmin,
    });
  },
);

const updateMemberQuestionnaire = createAsyncThunk(
  'assignment/updateMemberQuestionnaire',
  async (data: SaveQuestionnaireOptions): Promise<AssignmentInfo> => {
    const { assignmentId, quesitonnaireData } = data;

    await adminApiClient.updateAssignmentQuestionnaireData(
      assignmentId,
      quesitonnaireData,
    );

    const primaryInfo = await adminApiClient.getAssignmentDetail(assignmentId);

    return { primaryInfo: primaryInfo as AssignmentInfoAdminResponseModel };
  },
);

const saveClassificationQuestionnaire = createAsyncThunk(
  'assignment/saveClassificationQuestionnaire',
  async (data: SaveQuestionnaireOptions) => {
    const { isAdmin, assignmentId, assignmentKey, quesitonnaireData } = data;

    isAdmin
      ? await adminApiClient.fillContractorQuestionnaireData(
          assignmentId,
          quesitonnaireData,
        )
      : await backofficeApiClient.fillСontractorQuestionnaireData(
          assignmentId,
          quesitonnaireData,
        );

    return getAssignmentDetailsByRole({
      assignmentId,
      assignmentKey,
      isAdminRole: isAdmin,
    });
  },
);

const updateClassificationQuestionnaire = createAsyncThunk(
  'assignment/updateClassificationQuestionnaire',
  async (data: SaveQuestionnaireOptions): Promise<AssignmentInfo> => {
    const { assignmentId, assignmentKey, quesitonnaireData } = data;

    await adminApiClient.updateContractorQuestionnaireData(
      assignmentId,
      quesitonnaireData,
    );

    const primaryInfo =
      await adminApiClient.getAssignmentDetailByKey(assignmentKey);

    return { primaryInfo: primaryInfo as AssignmentInfoAdminResponseModel };
  },
);

interface RenewAssignmentOptions {
  isAdminRole: boolean;
  data: RenewAssignmentModel;
}

const renewAssignmentWithoutChanges = createAsyncThunk(
  'assignment/renewAssignmentWithoutChanges',
  async ({ isAdminRole, data }: RenewAssignmentOptions) => {
    return isAdminRole
      ? adminApiClient.renewAssignmentWithoutChanges(data)
      : backofficeApiClient.renewAssignmentWithoutChanges(data);
  },
);

interface RenewAssignmentWithChangesOptions {
  isAdminRole: boolean;
  data: AssignmentPrimaryDocumentEditModel;
}
const renewAssignmentWithChanges = createAsyncThunk(
  'assignment/renewAssignmentWithChanges',
  async (
    { data, isAdminRole }: RenewAssignmentWithChangesOptions,
    { rejectWithValue },
  ) => {
    try {
      const response = isAdminRole
        ? await adminApiClient.renewAssignmentWithChanges(data)
        : await backofficeApiClient.renewAssignmentWithChanges(data);

      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

interface AssignmentRehireOptions {
  isAdminRole: boolean;
  data: AssignmentPrimaryDocumentEditModel;
}

const rehireAssignmentWithSameCustomer = createAsyncThunk(
  'assignment/rehireAssignmentWithSameCustomer',
  async (
    { data, isAdminRole }: AssignmentRehireOptions,
    { rejectWithValue },
  ) => {
    try {
      const response = isAdminRole
        ? await adminApiClient.rehireAssignmentWithChanges(
            RenewKindEnum.Rehire,
            {
              ...data,
              renewType: RenewAssignmentEnum.WithChanges,
            },
          )
        : await backofficeApiClient.rehireAssignmentWithChanges(
            RenewKindEnum.Rehire,
            {
              ...data,
              renewType: RenewAssignmentEnum.WithChanges,
            },
          );

      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const rehireAssignmentWithNewCustomer = createAsyncThunk(
  'assignment/rehireAssignmentWithNewCustomer',
  async (
    { data, isAdminRole }: AssignmentRehireOptions,
    { rejectWithValue },
  ) => {
    try {
      const response = isAdminRole
        ? await adminApiClient.rehireAssignmentWithChanges(
            RenewKindEnum.Rehire,
            {
              ...data,
              renewType: RenewAssignmentEnum.WithChangeCustomer,
            },
          )
        : await backofficeApiClient.rehireAssignmentWithChanges(
            RenewKindEnum.Rehire,
            {
              ...data,
              renewType: RenewAssignmentEnum.WithChangeCustomer,
            },
          );

      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const rehireAssignmentWithoutChanges = createAsyncThunk(
  'assignment/rehireAssignmentWithoutChanges',
  async ({ data, isAdminRole }: RenewAssignmentOptions) => {
    return isAdminRole
      ? adminApiClient.renewAssignmentWithoutChanges(data)
      : backofficeApiClient.renewAssignmentWithoutChanges(data);
  },
);

interface GetAssignmentsDetailExcelModel {
  assignmentId?: string;
  isAdminRole?: boolean;
}

const getAssignmentsDetailExcel = createAsyncThunk(
  'assignment/getAssignmentsDetailExcel',
  async ({ assignmentId, isAdminRole }: GetAssignmentsDetailExcelModel) => {
    const result = isAdminRole
      ? await adminApiClient.getAssignmentsDetailExcel(assignmentId as string)
      : await backofficeApiClient.getAssignmentsDetailExcel(
          assignmentId as string,
        );
    const url = URL.createObjectURL(result.data);
    return { url, fileName: result.fileName };
  },
);

const getAssignmentImportsInfo = createAsyncThunk(
  'assignment/getAssignmentImportsInfo',
  async (assignmentId: string) => {
    return adminApiClient.getAssignmentImportsInfo(assignmentId);
  },
);

interface AssignmentUKGImportOptions {
  assignmentId: string;
  type: KronosImportTypeEnum;
}

const getAssignmentUKGImport = createAsyncThunk(
  'assignment/getAssignmentUKGImport',
  async ({ assignmentId, type }: AssignmentUKGImportOptions) => {
    const result = await adminApiClient.getImport(assignmentId, type);

    const url = URL.createObjectURL(result.data);
    return { url, fileName: result.fileName };
  },
);

const getAssignmentImportResult = createAsyncThunk(
  'assignment/getAssignmentImportResult',
  async ({ assignmentId, type }: AssignmentUKGImportOptions) => {
    const result = await adminApiClient.getAssignmentImportResult(
      assignmentId,
      type,
    );

    const url = URL.createObjectURL(result.data);
    return { url, fileName: result.fileName };
  },
);

const getW9InfoFileForDraftAssignment = createAsyncThunk(
  'assignment/getW9InfoFileForDraftAssignment',
  async (assignmentId: string) => {
    return backofficeApiClient.getDraftWorkerFile(assignmentId);
  },
);

interface TeammatesWithParentOptions {
  isAdmin?: boolean;
  companyId?: string;
}

const getPartnerAndChildUsersList = createAsyncThunk(
  'assignment/getPartnerAndChildUsersList',
  async ({ isAdmin, companyId }: TeammatesWithParentOptions) => {
    return isAdmin
      ? adminApiClient.getTeammatesWithParent(companyId)
      : backofficeApiClient.getTeammatesWithParent();
  },
);

const updateKronosStatus = createAsyncThunk(
  'assignment/updateKronosStatus',
  async (assignmentId: string) => {
    return adminApiClient.markResponsed(assignmentId);
  },
);

const getAssignmentTemplateDetails = createAsyncThunk(
  'assignment/getAssignmentTemplateDetails',
  async (templateId: string) => {
    return backofficeApiClient.getTemplateDetails(templateId);
  },
);

const getPartnerAssignmentDetails = createAsyncThunk(
  'assignment/getPartnerAssignmentDetails',
  async (assignmentKey: string) => {
    return backofficeApiClient.getSplittedWithMeAssignmentDetailByKey(
      assignmentKey,
    );
  },
);

const getEditInfoForAssignmentTemplate = createAsyncThunk(
  'assignment/getEditInfoForAssignmentTemplate',
  async ({ assignmentId }: GetEditAssignmentModel) => {
    return backofficeApiClient.getTemplateEditModel(assignmentId);
  },
);

const getAssignmentTemplateNames = createAsyncThunk(
  'assignment/getAssignmentTemplateNames',
  async () => {
    return backofficeApiClient.getTemplateNames();
  },
);

const deleteAssignmentTemplate = createAsyncThunk(
  'assignment/deleteAssignmentTemplate',
  async (templateId: string) => {
    return backofficeApiClient.deleteAssignmentTemplate(templateId);
  },
);

const deleteAssignmentDraft = createAsyncThunk(
  'assignment/deleteAssignmentDraft',
  async (assignmentId: string) => {
    return backofficeApiClient.deleteAssignmentDraft(assignmentId);
  },
);

interface GetAssignmentsTemplate {
  pageNumber: number;
  requestId: number;
}

const getAssignmentTemplates = createAsyncThunk(
  'assignment/getAssignmentTemplates',
  async ({ pageNumber }: GetAssignmentsTemplate) => {
    return backofficeApiClient.getAssignmentTemplates(pageNumber);
  },
);

interface GetPartnerAssignments {
  pageNumber: number;
  requestId: number;
  searchQuery?: string;
}

const getPartnerAssignments = createAsyncThunk(
  'assignment/getPartnerAssignments',
  async ({ pageNumber, searchQuery }: GetPartnerAssignments) => {
    return backofficeApiClient.getSplittedWithMeAssignments(
      pageNumber,
      searchQuery,
    );
  },
);

const getPartnerAssignmentExcel = createAsyncThunk(
  'assignment/getPartnerAssignmentExcel',
  async ({ assignmentId }: GetAssignmentsDetailExcelModel) => {
    const result =
      await backofficeApiClient.getSplittedWithMeAssignmentsDetailExcel(
        assignmentId as string,
      );
    const url = URL.createObjectURL(result.data);
    return { url, fileName: result.fileName };
  },
);

const createNewTemplateAssignment = createAsyncThunk(
  'assignment/createNewTemplateAssignment',
  async (data: AssignmentPrimaryDocumentEditModel, { rejectWithValue }) => {
    try {
      return await backofficeApiClient.createNewDraftAssignment(data);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

interface HistoryForCustomerModel {
  entityId: string;
  pageNumber: number;
}
const getHistoryDataForAssignment = createAsyncThunk(
  'assignment/getHistoryDataForAssignment',
  async ({ entityId, pageNumber }: HistoryForCustomerModel) => {
    return adminApiClient.getAssignmentHistory(entityId, 20, pageNumber);
  },
);

interface UpdateFinancialSchedulerInfoModel {
  assignmentId: string;
  details: AssignmentFinancialSchedulerEditModel;
  isAdminRole?: boolean;
}
const updateFinancialSchedulerInfo = createAsyncThunk(
  'assignment/updateFinancialSchedulerInfo',
  async ({
    assignmentId,
    details,
    isAdminRole,
  }: UpdateFinancialSchedulerInfoModel) => {
    return isAdminRole
      ? adminApiClient.updateFinanticalSchedulerInfo(assignmentId, details)
      : backofficeApiClient.updateFinanticalSchedulerInfo(
          assignmentId,
          details,
        );
  },
);

interface RemoveFinancialSchedulerInfoOptions {
  assignmentId: string;
  isAdminRole?: boolean;
}

const removeFinancialSchedulerInfo = createAsyncThunk(
  'assignment/removeFinancialSchedulerInfo',
  async ({
    assignmentId,
    isAdminRole,
  }: RemoveFinancialSchedulerInfoOptions) => {
    return isAdminRole
      ? adminApiClient.removeFinanticalSchedulerInfo(assignmentId)
      : backofficeApiClient.removeFinanticalSchedulerInfo(assignmentId);
  },
);

const getAssignmentBulkImportTemplate = createAsyncThunk(
  'assignment/getAssignmentBulkImportTemplate',
  async () => {
    return await adminApiClient.getTemplateFile(
      FileTypeEnum.AssignmentCreationTemplate,
    );
  },
);

const preUploadBulkImportAssignments = createAsyncThunk(
  'assignment/preUploadBulkImportAssignments',
  async (file: any, { rejectWithValue }) => {
    try {
      return await adminApiClient.importAssignmentListFromPlainTable(file);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const bulkImportAssignments = createAsyncThunk(
  'assignment/bulkImportAssignments',
  async (data: AssignmentBulkImportViewModel[]) => {
    return adminApiClient.createAssignments(data);
  },
);

const assignmentSlice = createSlice({
  name: 'assignment',
  initialState,
  reducers: {
    handleTemplateAssignmentModalOpen: (
      state,
      action: PayloadAction<boolean>,
    ) => ({
      ...state,
      isTemplateAssignmentModalOpen: action.payload,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      Object.assign(state, initialState);
    });
    builder.addCase(createNewAssignment.fulfilled, (state) => {
      state.assignmentCreatedAt = Date.now();
    });
    builder.addCase(createNewDraftAssignment.fulfilled, (state) => {
      state.assignmentDraftCreatedAt = Date.now();
    });
    builder.addCase(createNewTemplateAssignment.fulfilled, (state) => {
      state.assignmentTemplateCreatedAt = Date.now();
    });
    builder.addCase(getJobTitles.fulfilled, (state, action) => {
      state.jobTitles = action.payload;
      state.isJobTitlesLoading = false;
    });
    builder.addCase(getJobTitles.pending, (state, action) => {
      state.isJobTitlesLoading = true;
    });
    builder.addCase(getJobTitles.rejected, (state, action) => {
      state.isJobTitlesLoading = false;
    });
    builder.addCase(getAllWorkCodes.fulfilled, (state, action) => {
      state.workCodes = action.payload;
      state.isWorkCodesLoading = false;
    });
    builder.addCase(getAllWorkCodes.pending, (state, action) => {
      state.isWorkCodesLoading = true;
    });
    builder.addCase(getAllWorkCodes.rejected, (state, action) => {
      state.isWorkCodesLoading = false;
    });
    builder.addCase(getAssignmentTypesPayCode.fulfilled, (state, action) => {
      state.typesPayCode = action.payload;
      state.isTypesPayCodeLoading = false;
    });
    builder.addCase(getAssignmentTypesPayCode.pending, (state, action) => {
      state.typesPayCode = action.payload;
      state.isTypesPayCodeLoading = true;
    });
    builder.addCase(getAssignmentTypesPayCode.rejected, (state, action) => {
      state.typesPayCode = undefined;
      state.isTypesPayCodeLoading = false;
    });
    builder.addCase(getAssignmentPayCodeGroups.fulfilled, (state, action) => {
      state.payCodesGroups = action.payload;
      state.isPayCodesGroupsLoading = false;
    });
    builder.addCase(getAssignmentPayCodeGroups.pending, (state, action) => {
      state.isPayCodesGroupsLoading = true;
    });
    builder.addCase(getAssignmentPayCodeGroups.rejected, (state, action) => {
      state.payCodesGroups = undefined;
      state.isPayCodesGroupsLoading = false;
    });
    builder.addCase(getPayCodesDetailsInfo.fulfilled, (state, action) => {
      state.payCodes = action.payload;
      state.isPayCodeTypesLoading = false;
    });
    builder.addCase(getPayRateTypes.fulfilled, (state, action) => {
      state.payRateTypes = action.payload;
      state.isPayCodeTypesLoading = false;
    });
    builder.addCase(getPayRateTypes.pending, (state, action) => {
      state.isPayCodeTypesLoading = true;
    });
    builder.addCase(getPayRateTypes.rejected, (state, action) => {
      state.isPayCodeTypesLoading = false;
    });
    builder.addCase(getAssignmentPrimaryDetailsByKey.pending, (state) => {
      state.assignmentDetails = undefined;
    });
    builder.addCase(
      getAssignmentPrimaryDetailsByKey.fulfilled,
      (state, action) => {
        state.assignmentDetails = action.payload;
      },
    );
    builder.addCase(getAssignmentPrimaryDetailsByKey.rejected, (state) => {
      state.assignmentDetails = undefined;
    });
    builder.addCase(saveMemberQuestionnaire.fulfilled, (state, action) => {
      state.assignmentDetails = action.payload.primaryInfo;
    });
    builder.addCase(
      saveClassificationQuestionnaire.fulfilled,
      (state, action) => {
        state.assignmentDetails = action.payload.primaryInfo;
      },
    );
    builder.addMatcher(
      isAnyActionOf(
        // Reload history section on assignment's sections updates
        updateDraftAssignment.fulfilled,
        updateAssignmentByAdmin.fulfilled,
        updateAssignmentAdditionalInfo.fulfilled,
        approveAssignmentProgressStep.fulfilled,
        completeAssignmentProgressStep.fulfilled,
        sendAssignmentAdditionalInfoResponse.fulfilled,
        saveClassificationQuestionnaire.fulfilled,
        saveMemberQuestionnaire.fulfilled,
      ),
      (state) => {
        state.assignmentDetailsUpdatedAtByAdmin = Date.now();
      },
    );
  },
});

const { reducer, actions } = assignmentSlice;
const { handleTemplateAssignmentModalOpen } = actions;

export {
  getJobTitles,
  getAssignments,
  createNewAssignment,
  approveAssignmentProgressStep,
  createNewAssignmentFromDraft,
  createNewDraftAssignment,
  getAssignmentPrimaryDetails,
  getAssignmentPrimaryDetailsByKey,
  getAssignmentsCompanies,
  getCustomersByAssignments,
  completeAssignmentProgressStep,
  createCommentForAssignment,
  getCommentsForAssignment,
  getEditInfoForAssignment,
  updateAssignment,
  updateDraftAssignment,
  sendAssignmentAdditionalInfoResponse,
  getAssignmentAdminAdditionalInfo,
  getAssignmentAdditionalInfo,
  updateAssignmentAdditionalInfo,
  saveMemberQuestionnaire,
  updateMemberQuestionnaire,
  saveClassificationQuestionnaire,
  updateClassificationQuestionnaire,
  sendMemberQuestionnaireNotification,
  renewAssignmentWithChanges,
  renewAssignmentWithoutChanges,
  getRenewInfoForAssignment,
  sendClassificationQuestionnaireNotification,
  getAssignmentsDetailExcel,
  hideAssignmentPulsatingIndicator,
  getW9InfoFileForDraftAssignment,
  getPartnerAndChildUsersList,
  updateKronosStatus,
  getEditInfoForAssignmentTemplate,
  getAssignmentTemplateDetails,
  getAssignmentTemplateNames,
  handleTemplateAssignmentModalOpen,
  getAssignmentTemplates,
  createNewTemplateAssignment,
  getHistoryDataForAssignment,
  updateAssignmentByAdmin,
  getEditInfoForAssignmentByAdmin,
  getEditDraftInfoForAssignment,
  updateFinancialSchedulerInfo,
  removeFinancialSchedulerInfo,
  deleteAssignmentTemplate,
  updateTemplate,
  getWorkersByAssignments,
  deleteAssignmentDraft,
  getPartnerAssignments,
  getPartnerAssignmentDetails,
  getPartnerAssignmentExcel,
  getAssignmentBulkImportTemplate,
  preUploadBulkImportAssignments,
  bulkImportAssignments,
  getAssignmentUKGImport,
  getAssignmentImportsInfo,
  getAssignmentImportResult,
  saveTerminateAssignment,
  getAssignmentTerminationModel,
  getAssignmentTerminationAttachmentFileAll,
  updateAssignmentTermination,
  deleteAssignmentTermination,
  getPayRateTypes,
  getPayCodesDetailsInfo,
  rehireAssignmentWithoutChanges,
  rehireAssignmentWithSameCustomer,
  rehireAssignmentWithNewCustomer,
  getAssignmentTerminationAttachmentFile,
  getAssignmentSupportingDocumentFile,
  getAssignmentListExcel,
  getAllWorkCodes,
  getAssignmentTypesPayCode,
  updateCommentForAssignment,
  getAssignmentPayCodeGroups,
};
export default reducer;
